/**
 * The uploads in progress, kept for the whole app rather than the Import
 * page, so they carry on while the user works elsewhere.
 *
 * @module
 */
import { useSyncExternalStore } from "react";
import type { Upload } from "tus-js-client";
import { api, unwrap } from "../api/client";
import type { Picked } from "./picked";
import { begin, createUpload } from "./upload";

export interface Batch {
  id: string;
  libraryId: string;
}

export interface Transfer {
  key: string;
  batchId: string;
  name: string;
  folder?: string;
  size: number;
  sent: number;
  /** Queued behind the files before it; nothing sent yet. */
  waiting?: boolean;
  error?: string;
  upload?: Upload;
}

interface State {
  /** The import opened in this session, before the server lists it as open. */
  opened: Batch | null;
  transfers: Transfer[];
  /** Counts finished uploads, so pages showing a batch know to refresh. */
  finished: number;
}

let state: State = { opened: null, transfers: [], finished: 0 };
const listeners = new Set<() => void>();

function set(change: (s: State) => State) {
  state = change(state);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTransfers(): State {
  return useSyncExternalStore(subscribe, () => state);
}

export function isUploading(): boolean {
  return state.transfers.some((t) => !t.error);
}

export function setOpened(opened: Batch | null) {
  set((s) => ({ ...s, opened }));
}

function update(key: string, change: Partial<Transfer>) {
  set((s) => ({ ...s, transfers: s.transfers.map((t) => (t.key === key ? { ...t, ...change } : t)) }));
}

function drop(key: string) {
  set((s) => ({ ...s, transfers: s.transfers.filter((t) => t.key !== key) }));
}

/**
 * Uploads `files` into `batch`, or into a new import for `libraryId`. Runs
 * outside any page, so leaving the Import page part-way loses nothing.
 * `resumeUrl(file)`: the server's unfinished upload of that file, if any.
 */
export async function upload(
  files: Picked[],
  batch: Batch | null,
  libraryId: string,
  resumeUrl: (file: File) => string | undefined,
): Promise<void> {
  let current = batch;
  if (!current) {
    const opened = await unwrap(api.POST("/api/imports", { body: { library_id: libraryId } }));
    current = { id: opened.id, libraryId: opened.library_id };
    setOpened(current);
  }
  await send(files, current.id, resumeUrl);
}

async function send(files: Picked[], batchId: string, resumeUrl: (file: File) => string | undefined) {
  // Listed at once, so a large folder shows everything that is coming.
  const queued = files.map(({ file, folder }) => ({
    file,
    folder,
    key: `${folder ?? ""}/${file.name}:${file.size}:${file.lastModified}:${Math.random()}`,
  }));
  set((s) => ({
    ...s,
    transfers: [
      ...s.transfers,
      ...queued.map(({ key, file, folder }) => ({
        key,
        batchId,
        name: file.name,
        folder,
        size: file.size,
        sent: 0,
        waiting: true,
      })),
    ],
  }));
  for (const { file, folder, key } of queued) {
    if (!state.transfers.some((t) => t.key === key)) continue;
    update(key, { waiting: false });
    // Attached before anything async, so Cancel can always reach it.
    const upload = createUpload(
      file,
      folder,
      batchId,
      {
        onProgress: (sent) => update(key, { sent, error: undefined }),
        onDone: () => {
          drop(key);
          set((s) => ({ ...s, finished: s.finished + 1 }));
        },
        onError: (error) => update(key, { error }),
      },
      resumeUrl(file),
    );
    update(key, { upload });
    await begin(upload, batchId, () => !state.transfers.some((t) => t.key === key));
  }
}

export function retry(key: string) {
  const t = state.transfers.find((x) => x.key === key);
  update(key, { error: undefined });
  t?.upload?.start();
}

/** Stops the upload and has the server delete what it received. */
export async function cancel(key: string) {
  const t = state.transfers.find((x) => x.key === key);
  drop(key);
  await t?.upload?.abort(true).catch(() => undefined);
  set((s) => ({ ...s, finished: s.finished + 1 }));
}

/** On sign-out: stops everything without deleting it, so it can resume later. */
export function reset() {
  state.transfers.forEach((t) => void t.upload?.abort(false));
  set(() => ({ opened: null, transfers: [], finished: state.finished }));
}

if (typeof window !== "undefined") {
  // Back online: carry on with uploads that gave up while offline.
  window.addEventListener("online", () => {
    state.transfers.filter((t) => t.error).forEach((t) => retry(t.key));
  });
  // Leaving or reloading the page stops uploads; ask first.
  window.addEventListener("beforeunload", (e) => {
    if (isUploading()) e.preventDefault();
  });
}
